// A manager's invite link into the mini app (`?startapp=i_<code>`).
//
// The backend's `messaging/invites.py` counts, per manager and fund, who
// opened the link and who it actually brought onto Bale/Eitaa. The code
// arrives with the launch, but it can only be reported with a session — which
// a first-time visitor gets only after the SMS login and the account link. So
// it is kept here until then, and reported with retries: a request fired as a
// mini app opens can vanish (Bale desktop, 2026-09-18).

import { miniappReportInvite } from 'services/miniapp'
import { queueEvent } from 'utilities/analytics'
import { getRememberedPlatform, getStartParam } from './detect'

/** Must match `messaging.invites.START_PREFIX`. */
export const INVITE_START_PARAM_PREFIX = 'i_'

const CODE_PATTERN = /^[A-Za-z0-9]{4,12}$/
const STORAGE_KEY = 'hamyan.miniappInvite'
/** An invite not reported within this long is dropped: it brought nobody. */
const KEEP_MS = 7 * 24 * 60 * 60 * 1000
export const REPORT_DELAYS_MS = [0, 1500, 4000]

export const inviteCodeFromStartParam = (): string | null => {
    const param = getStartParam()
    if (!param || !param.startsWith(INVITE_START_PARAM_PREFIX)) return null
    const code = param.slice(INVITE_START_PARAM_PREFIX.length)
    return CODE_PATTERN.test(code) ? code : null
}

export const rememberInvite = (code: string): void => {
    try {
        window.localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify({ code, at: Date.now() })
        )
    } catch {
        /* private mode: the visit simply goes uncounted */
    }
}

const pendingInvite = (): string | null => {
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return null
        const saved = JSON.parse(raw) as { code?: string; at?: number }
        if (
            !saved.code ||
            !CODE_PATTERN.test(saved.code) ||
            !saved.at ||
            Date.now() - saved.at > KEEP_MS
        ) {
            window.localStorage.removeItem(STORAGE_KEY)
            return null
        }
        return saved.code
    } catch {
        return null
    }
}

const forget = () => {
    try {
        window.localStorage.removeItem(STORAGE_KEY)
    } catch {
        /* nothing to do */
    }
}

const wait = (ms: number) =>
    new Promise<void>((resolve) => window.setTimeout(resolve, ms))

let inFlight: Promise<void> | null = null

/**
 * Report the kept invite, if there is one. Needs a session. Safe to call on
 * every launch and after every login: one request at a time, and the code is
 * forgotten once the backend has it.
 */
export const reportPendingInvite = (
    delays: number[] = REPORT_DELAYS_MS
): Promise<void> => {
    if (inFlight) return inFlight
    const code = pendingInvite()
    const platform = getRememberedPlatform()
    if (!code || !platform) return Promise.resolve()
    inFlight = (async () => {
        for (const delay of delays) {
            if (delay) await wait(delay)
            try {
                const answer = await miniappReportInvite(code, platform)
                forget()
                queueEvent(
                    answer?.joined
                        ? 'miniapp_invite_joined'
                        : 'miniapp_invite_reported',
                    { invite_code: code, platform }
                )
                return
            } catch {
                /* try again */
            }
        }
    })().finally(() => {
        inFlight = null
    })
    return inFlight
}
